import React from 'react'

// Utils
import dateFormat from 'utils/localizedDateFormat'
import { PLAN_TYPE_LABEL } from 'modules/workspace/utils'

// Components
import RowGroup from '../RowGroup'

// Styles
import { ErrorPill, Description, StyledButton } from './BillingCycle.styles'
import { Row, ContentCell } from '../PlanOverviewPanel/PlanOverviewPanel.styles'

// Types
import { BillingSeatsInfoFragment, BillingPlanFragment } from 'gqlTypes'

interface BillingCycleProps {
  subscriptionEnd?: string
  nextBillingCycleDate?: string
  seats: BillingSeatsInfoFragment
  isWorkspaceOnTrial: boolean
  customerHasPaymentDetails: boolean
  currentPlan?: BillingPlanFragment

  onCancelSeatScheduleChange: () => void
}

// Helpers
const getHeaderCopy = (isWorkspaceOnTrial: boolean) =>
  isWorkspaceOnTrial ? 'First Billing Date' : 'Next Billing Date'

/**
 * BillingCycle
 *
 * Rends the "Next Billing Cycle" panel in the Plan Overview Panel (Workspace Settings)
 */
const BillingCycle: React.FC<BillingCycleProps> = props => {
  const {
    subscriptionEnd,
    nextBillingCycleDate,
    seats,
    isWorkspaceOnTrial,
    onCancelSeatScheduleChange,
    customerHasPaymentDetails,
    currentPlan,
  } = props

  const isScheduled = !!seats.scheduledSeatsTotal

  if (subscriptionEnd) {
    return (
      <Row>
        <RowGroup.HeadingCell width={['70%', 1 / 4]}>
          Next Billing Date
        </RowGroup.HeadingCell>
        <ContentCell data-testid="plan-next-cycle">
          <div>
            <ErrorPill data-testid="warning-subscription-badge">
              Subscription Canceled
            </ErrorPill>
            <br />
            <span>
              You can keep using the Workspace until{' '}
              {dateFormat(new Date(subscriptionEnd))}
            </span>
          </div>
        </ContentCell>
      </Row>
    )
  }

  return (
    <Row>
      <RowGroup.HeadingCell width={['70%', 1 / 4]}>
        {getHeaderCopy(isWorkspaceOnTrial)}
      </RowGroup.HeadingCell>
      <ContentCell data-testid="plan-next-cycle">
        <Description>
          <strong>{dateFormat(new Date(nextBillingCycleDate!))}</strong>
          <br />
          {isWorkspaceOnTrial && !customerHasPaymentDetails && (
            <>If you subscribe, you’ll be billed on this date</>
          )}
          {isWorkspaceOnTrial && customerHasPaymentDetails && (
            <>
              You&apos;ll be billed{' '}
              {PLAN_TYPE_LABEL[currentPlan!.type].toLowerCase()} for Editor
              Seats you&apos;re using at the end of your trial.
              <br />
              <br />
            </>
          )}
          {!isWorkspaceOnTrial && isScheduled && (
            <>
              You have scheduled a change to have {seats.scheduledSeatsTotal}{' '}
              Editor seats on the date above.{' '}
              <StyledButton onClick={onCancelSeatScheduleChange}>
                Cancel scheduled change?
              </StyledButton>
              <br />
              <br />
            </>
          )}
        </Description>
      </ContentCell>
    </Row>
  )
}

export default BillingCycle
